import { useCallback, useEffect, useState } from 'react';
import { HiOutlineCircleStack } from 'react-icons/hi2';
import ConfirmDialog from "../../../components/confirm-dialog/confirm-dialog";
import DataResourceList from '../../../components/data-resource-list';
import { useAppInfo } from '../../../helpers/hooks/common-hook';
import { getMyAccount } from '../../../services/account/account-service';
import { approveDataSource, deleteDataSources, listDataSources, viewDataSource } from '../../../services/knowledge';
import DataSourceConnectorFileModel from "../data-source-connectors/file-modal";
import TestModal from "./test-modal";
import OcrReviewModal from "./ocr-review-modal";

const statusMap = {
  'preparing': {
    color: 'bg-gray-100 text-gray-600',
    dot: 'bg-gray-400',
    title: 'Preparing'
  },
  'scheduled': {
    color: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
    dot: 'bg-blue-500',
    title: 'Scheduled'
  },
  'ready': {
    color: 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300',
    dot: 'bg-green-500',
    title: 'Ready'
  },
  'error': {
    color: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
    dot: 'bg-red-500',
    title: 'Issue'
  }
};

const dbTypeMap = {
  'postgresql': 'PostgreSQL',
  'mysql': 'MySQL'
};

function StatusBadge({ className, dotClassName, children }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${className}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dotClassName}`} />
      {children}
    </span>
  );
}

function ModelStatusComp({ modelStatus }) {
  const status = statusMap[modelStatus];
  if (!status) return null;
  return (
    <StatusBadge className={status.color} dotClassName={status.dot}>
      {status.title}
    </StatusBadge>
  );
};

export default function DataSourceManage() {
  const { appId, version } = useAppInfo();
  const [userRole, setUserRole] = useState(null);
  const isAdmin = userRole === 'admin';
  const [items, setItems] = useState([]);

  const [formState, setFormState] = useState({
    processing: true,
    success: false,
    failed: false,
    errorMessage: ''
  });

  const [openConnSelectorDialog, setOpenConnSelectorDialog] = useState(false);
  const [editKnowledge, setEditKnowledge] = useState(null);
  const [deletingKnowledge, setDeletingKnowledge] = useState({});
  const [openDeleteConfirmDialog, setDeleteRevokeConfirmDialog] = useState(false);
  const [testingDataSource, setTestingDataSource] = useState(null);
  const [ocrReviewDataSource, setOcrReviewDataSource] = useState(null);
  const [reviewingKnowledge, setReviewingKnowledge] = useState({});
  const [openReviewConfirmDialog, setOpenReviewConfirmDialog] = useState(false);

  const fetchConfigs = useCallback(async () => {
    setFormState({ processing: true, failed: false, errorMessage: '' });
    try {
      const { response, status } = await listDataSources(appId, version);
      if (status !== 200 || !Array.isArray(response?.data)) throw new Error();
      setItems(response.data);
      setFormState({ processing: false, failed: false, errorMessage: '' });
    } catch (_) {
      setFormState({ processing: false, failed: true, errorMessage: 'Unable to load data sources. Please try again.' });
    }
  }, [appId, version]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);
  useEffect(() => {
    getMyAccount().then(({ account }) => setUserRole(account?.role || null)).catch(() => setUserRole(null));
  }, []);

  useEffect(() => {
    if (deletingKnowledge && deletingKnowledge.id) {
      setDeleteRevokeConfirmDialog(true);
    }
  }, [deletingKnowledge]);


  const updateFormState = (processing, success, failed, errorMessage) => {
    setFormState(Object.assign({}, { processing, success, failed, errorMessage }));
  };

  const handleOpenConnSelector = () => {
    setOpenConnSelectorDialog(true);
  };

  const handleOnCancelConnectorSelect = (state) => {
    setOpenConnSelectorDialog(false);
    setEditKnowledge(null)
    if (state) {
      fetchConfigs();
    }
  };

  const handleSelectDelete = (id, returnOriginal) => {
    setDeletingKnowledge({ id, returnOriginal });
  };

  const handleSelectEdit = (editingData) => {
    setEditKnowledge(editingData);
  };

  const handleSelectReview = (item) => {
    setReviewingKnowledge(item);
    setOpenReviewConfirmDialog(true);
  };

  const handleConfirmReview = async confirmed => {
    setOpenReviewConfirmDialog(false);
    if (confirmed) {
      setFormState({ processing: true, failed: false, errorMessage: '' });
      try {
        const { status, response } = await approveDataSource(appId, reviewingKnowledge.dataSourceId);
        if (status !== 200) {
          throw new Error(response);
        }
        setReviewingKnowledge({});
        await fetchConfigs();
      } catch (e) {
        setFormState({ processing: false, failed: true, errorMessage: e.message });
      }
    } else {
      setReviewingKnowledge({});
    }
  };

  const handleView = async (sourceId) => {
    const { response, status } = await viewDataSource(appId, sourceId);
    if (status === 200) {
      window.open(response.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleConfirmDelete = async confirmed => {
    setDeleteRevokeConfirmDialog(false);
    if (confirmed) {
      updateFormState(true, false, false);
      try {
        const result = await deleteDataSources(appId, deletingKnowledge.id, deletingKnowledge.returnOriginal);
        if (result?.status && (result.status < 200 || result.status >= 300)) throw new Error();
        setDeletingKnowledge({});
        await fetchConfigs();
      } catch (_) {
        updateFormState(false, false, true, 'Unable to delete this data source. Please try again.');
      }
    }
  };

  const processDataList = items.map(item => {
    const ocrNeedsReview = Boolean(item.ocrReviewRequired);
    const adminReviewPending = Boolean(item.adminReviewRequired || (item.reviewRequired && !ocrNeedsReview));
    const reviewRequired = Boolean(item.reviewRequired);
    const ocrEnabled = !!item.ocr?.enable;

    const isOcr = Boolean(item.ocrProcessed || ocrEnabled);

    const options = [
      {
        name: 'Edit',
        onClick: () => handleSelectEdit(item)
      },
      {
        name: 'View',
        onClick: () => handleView(item.dataSourceId)
      },
      {
        name: 'Test',
        onClick: () => setTestingDataSource(item)
      }
    ];

    // OCR Review available to all users (normal users + admins)
    if (isOcr || item.ocrReviewRequired) {
      options.push({
        name: 'OCR Review',
        onClick: () => setOcrReviewDataSource(item)
      });
    }

    // General KB approval strictly for admins (blocked if OCR review is pending)
    if (adminReviewPending && isAdmin && !ocrNeedsReview) {
      options.push({
        name: 'Approve',
        onClick: () => handleSelectReview(item)
      });
    }

    options.push({
      name: 'Delete',
      onClick: () => handleSelectDelete(item.dataSourceId, item.returnOriginal)
    });

    return {
      record: item,
      searchText: `${item.dataSourceId} ${item.dataSourceType || ''} ${item.fileType || item.dbType || ''}`,
      rowClassName: reviewRequired ? 'bg-yellow-50/60 dark:bg-yellow-950/20' : undefined,
      columnsData: [
        { data: <button type="button" onClick={() => handleSelectEdit(item)} className="rounded text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400">{item.dataSourceId}</button>, isIdField: true },
        {
          data: (
            <div className="flex min-w-[200px] max-w-sm flex-wrap items-center gap-1.5 py-0.5">
              <span className="inline-flex items-center rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-medium capitalize text-gray-600">
                {[item.dataSourceType, item.dataSourceType === 'file' ? item.fileType : (dbTypeMap[item.dbType] || item.dbType)].filter(Boolean).join(' · ') || 'Unknown'}
              </span>
              {(ocrEnabled || item.ocrProcessed || item.ocrReviewRequired) && (() => {
                const confScore = item.confidenceScore != null ? Number(item.confidenceScore) : null;
                const confPct = confScore != null ? Math.round(confScore * 100) : null;
                const ocrNeedsReview = Boolean(item.ocrReviewRequired);
                const badgeColor = ocrNeedsReview
                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 ring-1 ring-inset ring-amber-300 dark:ring-amber-700"
                  : confPct != null
                    ? confPct >= 85
                      ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                      : confPct >= 60
                        ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                        : "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                    : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300";
                return (
                  <button
                    type="button"
                    onClick={() => setOcrReviewDataSource(item)}
                    title={ocrNeedsReview ? "OCR review required - click to inspect and edit extracted text" : `OCR Pre-processing${confPct != null ? ` • ${confPct}% Confidence` : ""}`}
                    className="cursor-pointer transition hover:opacity-80"
                  >
                    <span
                      className={`inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-medium ${badgeColor}`}
                    >
                      {ocrNeedsReview ? (confPct != null ? `OCR • ${confPct}% (Review Needed)` : "OCR • Review Needed") : (confPct != null ? `OCR • ${confPct}%` : "OCR")}
                    </span>
                  </button>
                );
              })()}
            </div>
          )
        },
        {
          data: reviewRequired
            ? (isAdmin ? (
              ocrNeedsReview ? (
                <button
                  type="button"
                  onClick={() => setOcrReviewDataSource(item)}
                  title="Please complete the OCR review before granting admin approval"
                  className="cursor-pointer transition hover:opacity-80"
                >
                  <StatusBadge
                    className="bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                    dotClassName="bg-amber-500"
                  >
                    OCR Review Required
                  </StatusBadge>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleSelectReview(item)}
                  title="Click to approve this data source for use by your agent"
                  className="cursor-pointer transition hover:opacity-80"
                >
                  <StatusBadge
                    className="bg-yellow-50 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-300"
                    dotClassName="bg-yellow-500"
                  >
                    Review Pending
                  </StatusBadge>
                </button>
              )
            ) : (
              <StatusBadge
                className="bg-yellow-50 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-300"
                dotClassName="bg-yellow-500"
              >
                {ocrNeedsReview ? "OCR Review Required" : "Review Pending"}
              </StatusBadge>
            ))
            : <ModelStatusComp modelStatus={item.modelStatus} />
        },
        { data: item.modifiedAt, isUpdatedField: true },
        { options },
      ]
    }
  });

  return (
    <DataResourceList key={`${appId}:${version}`}
      title="Knowledge" description="Manage the sources your agent uses to answer questions."
      icon={HiOutlineCircleStack} items={processDataList} headers={['Source', 'Type', 'Status']}
      loading={formState.processing} error={formState.failed ? formState.errorMessage : ''} onRefresh={fetchConfigs}
      createLabel="Add data source" onCreate={handleOpenConnSelector}
      emptyTitle="Build your agent’s knowledge" emptyDescription="Add a file or connect a database to give your agent the information it needs to help your customers."
      filterLabel="All statuses" filters={[...Object.entries(statusMap).map(([value, status]) => ({ value, label: status.title })), { value: 'review', label: 'Review pending' }]}
      getFilterValue={item => item.reviewRequired && item.modelStatus === 'ready' ? 'review' : item.modelStatus}
    >
      {(openConnSelectorDialog) &&
        <DataSourceConnectorFileModel onCancel={handleOnCancelConnectorSelect} />
      }

      {(editKnowledge) &&
        <DataSourceConnectorFileModel
          editMode={true}
          onCancel={handleOnCancelConnectorSelect}
          savedDataSet={editKnowledge} />
      }
      {openDeleteConfirmDialog &&
        <ConfirmDialog
          open={openDeleteConfirmDialog}
          appId={appId}
          onCompletion={handleConfirmDelete}
          title="Delete data source"
          message={`Delete “${deletingKnowledge.id}”? This action cannot be undone.`}
        />
      }
      {openReviewConfirmDialog &&
        <ConfirmDialog
          open={openReviewConfirmDialog}
          appId={appId}
          onCompletion={handleConfirmReview}
          title="Approve data source"
          message="Approve this data source for use by your agent?"
        />
      }
      {ocrReviewDataSource && (
        <OcrReviewModal
          open={Boolean(ocrReviewDataSource)}
          appId={appId}
          dataSource={ocrReviewDataSource}
          isAdmin={isAdmin}
          onClose={() => setOcrReviewDataSource(null)}
          onApproved={fetchConfigs}
        />
      )}
      {testingDataSource &&
        <TestModal
          appId={appId}
          dataSource={testingDataSource}
          onClose={() => setTestingDataSource(null)}
        />
      }
    </DataResourceList>
  );
}
